import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import type { AppRegistryPage } from '@plato/app-meta/ai-meta';
import { DataTable } from '@plato/app-shared/components/data-table';
import { PageTitle } from '@plato/app-shared/components/page-title';
import { usePageAiMeta } from '@plato/app-shared/hooks/use-page-ai-meta';
import { SrSalesOrderType, type SrOrderStatus } from '@plato/data/starrocks/types';
import { QuoteIcon } from '@plato/ui-legacy/assets/icons/quote-icon';
import { DialogStateManager } from '@plato/ui-legacy/components/dialog/dialog-provider';
import type { QuickFilter } from '@plato/ui-legacy/components/filters/lib/filter-utils';
import { ListView as LegacyListView } from '@plato/ui-legacy/components/list-views/list-view';
import { TableFooterWithCount } from '@plato/ui-legacy/components/tables/table-footer-with-count';
import { Button } from '@plato/ui-legacy/components/ui/button';
import { Page } from '@plato/ui-legacy/components/ui/page';
import { PageHeader } from '@plato/ui-legacy/components/ui/page-header';
import { ListView, ListViewBody, ListViewHeader } from '@plato/ui/containers/list-view';
import { useBreakpoint } from '@plato/ui/util/use-breakpoint';
import { PlusIcon } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from '../../features/analytics/hooks/use-analytics.ts';
import { useDialogs } from '../../features/dialogs/all-dialogs.tsx';
import { quoteMobileColumns } from '../../features/list-views/columns/quote-order-mobile-columns.tsx';
import { useQuoteColumnsForListGrid } from '../../features/list-views/columns/use-quote-columns-for-list-grid.tsx';
import { useGenericListViewSorting } from '../../features/list-views/hooks/use-generic-list-view-sorting.tsx';
import { QuoteOrderListFilters } from '../../features/quote-order-list/components/quote-order-list-filters.tsx';
import type { QuoteOrderQueryFilters } from '../../features/quote-order-list/hooks/use-quote-order-list-filters.tsx';
import {
    openQuotesFilter,
    useQuoteOrderListFilters,
} from '../../features/quote-order-list/hooks/use-quote-order-list-filters.tsx';
import { useQuoteOrderListSummary } from '../../features/quote-order-list/hooks/use-quote-order-list-summary.tsx';
import { DraftOrderCreateDialog } from '../../features/sales-draft-new/views/create-draft-order-dialog.tsx';
import {
    quotesOrdersSortFields,
    SortOrder,
    type QuoteOrderSort,
    type QuotesOrdersSortBy,
} from '../../server/types/list-view-types.ts';
import { api } from '../../utils/api.ts';

export function useQuoteListAiMeta(): AppRegistryPage {
    const { t } = useTranslation();
    return usePageAiMeta(
        {
            path: '/quotes',
            title: t('page_title__sales_quotes', { ns: 'common' }),
            requires: ['canReadQuotes'],
            description:
                'All sales quotes from the ERP in one filterable list. Send the user here to find a ' +
                'quote or to see quotes by status, date, type, sales rep, storage or sales location or ' +
                'amount. The search box matches customer and quote id. Opening a quote shows its ' +
                'detail page. Some tenants do not have this page; if the user cannot see it, it is not ' +
                'enabled for them.',
        },
        useQuoteColumnsForListGrid(),
    );
}

const ORDER_TYPE = SrSalesOrderType.QUOTE;

function QuotesListView() {
    const { t } = useTranslation();
    const { transactionDraftAvailability, enableResponsiveTables } = useAppConfig();
    const sendEvent = useAnalytics();
    const listGridColumns = useQuoteColumnsForListGrid(enableResponsiveTables);

    const quickFilters = [
        {
            id: 'my-open-quotes',
            label: t('quick_filters__quotes_my_open_quotes', { ns: 'filters' }),
            showByDefault: true,
            filter: openQuotesFilter,
        },
    ] satisfies QuickFilter<QuoteOrderQueryFilters>[];

    const [appliedFilters, setAppliedFilters] = useQuoteOrderListFilters(ORDER_TYPE);

    const [sorting, sortChangedInInterface] = useGenericListViewSorting<QuotesOrdersSortBy>(
        appliedFilters.sortingFilter,
        (sortingFilter: QuoteOrderSort | null) => {
            setAppliedFilters((filters) => ({
                ...filters,
                sortingFilter: sortingFilter ?? undefined,
            }));
            if (sortingFilter) {
                sendEvent('quote_order_list_sort_used', {
                    column: sortingFilter.field,
                    direction: sortingFilter.direction === SortOrder.DESC ? 'DESC' : 'ASC',
                });
            }
        },
        quotesOrdersSortFields,
    );

    const isMobile = !useBreakpoint('sm');
    const { openOrderQuoteDetails } = useDialogs();
    const columns = enableResponsiveTables && isMobile ? quoteMobileColumns : listGridColumns;

    const { summary } = useQuoteOrderListSummary(ORDER_TYPE);

    const { data: aggregates, isLoading: isLoadingCount } =
        api.platoUser.getManyQuotesOrders.getAggregates.useQuery({
            ...appliedFilters,
            orderStatus: appliedFilters.orderStatus as SrOrderStatus[],
            orderTypes: [ORDER_TYPE],
        });
    const count = aggregates?.count;

    const {
        data: quotesListRawData,
        isLoading,
        isFetchingNextPage,
        hasNextPage,
        fetchNextPage,
        isSuccess: isDoneGettingList,
    } = api.platoUser.getManyQuotesOrders.getWithFilters.useInfiniteQuery(
        { ...appliedFilters, orderTypes: [ORDER_TYPE] },
        {
            getNextPageParam: (lastPage) => lastPage.nextCursor,
        },
    );

    const quotesList = useMemo(() => {
        if (quotesListRawData) {
            return quotesListRawData.pages.flatMap((page) => page.result);
        } else {
            return [];
        }
    }, [quotesListRawData]);

    const headerActions = transactionDraftAvailability !== 'UNAVAILABLE' && (
        <Button
            variant="primary"
            onClick={() => {
                void DialogStateManager.show(DraftOrderCreateDialog, {
                    draftType: 'QUOTE',
                    source: 'quote_list',
                });
            }}
            icon={PlusIcon}
        >
            <span>{t('top_navbar__new_draft')}</span>
        </Button>
    );

    const filters = (
        <QuoteOrderListFilters
            orderType={ORDER_TYPE}
            quickFilters={quickFilters}
            canGetFilters={isDoneGettingList}
        />
    );

    if (!enableResponsiveTables) {
        return (
            <Page>
                <PageTitle title={t('page_title__sales_quotes')} />
                <PageHeader
                    icon={QuoteIcon}
                    subTitle={t('page_sub_title__sales_quotes')}
                    title={t('page_title__sales_quotes')}
                    actions={headerActions}
                />
                {filters}
                <LegacyListView>
                    <LegacyListView.BodyContainer>
                        <DataTable
                            emptyLabel={t('empty_table__no_results')}
                            isLoading={isLoading}
                            isFetchingNextPage={isFetchingNextPage}
                            hasNextPage={hasNextPage}
                            onFetchNextPage={fetchNextPage}
                            data={quotesList}
                            columns={columns}
                            manualSorting
                            onSortingChange={sortChangedInInterface}
                            sorting={sorting}
                            summary={summary}
                        />
                        <TableFooterWithCount loading={isLoadingCount} count={count} />
                    </LegacyListView.BodyContainer>
                </LegacyListView>
            </Page>
        );
    }

    return (
        <ListView>
            <PageTitle title={t('page_title__sales_quotes')} />
            <ListViewHeader>
                <PageHeader
                    className="mt-0"
                    icon={QuoteIcon}
                    subTitle={t('page_sub_title__sales_quotes')}
                    title={t('page_title__sales_quotes')}
                    actions={headerActions}
                />
                {filters}
            </ListViewHeader>
            <ListViewBody>
                <DataTable
                    responsive
                    disableHeader={isMobile}
                    onRowClick={(row) => {
                        openOrderQuoteDetails(ORDER_TYPE, row.original.sales_order_id);
                    }}
                    emptyLabel={t('empty_table__no_results')}
                    isLoading={isLoading}
                    isFetchingNextPage={isFetchingNextPage}
                    hasNextPage={hasNextPage}
                    onFetchNextPage={fetchNextPage}
                    data={quotesList}
                    columns={columns}
                    manualSorting
                    onSortingChange={sortChangedInInterface}
                    sorting={sorting}
                    summary={summary}
                />
            </ListViewBody>
        </ListView>
    );
}

export default QuotesListView;
